import api from '@/lib/api';
import {
  TestResult,
  StudentPerformanceAnalytics,
  DetailedTestResult,
  TestResultsResponse,
  ResultsFilter
} from '../types/testResult';

/**
 * Service for test results and analytics
 */
class TestResultService {

  /**
   * Get all test results for the current student
   */
  async getStudentResults(page?: number, size?: number): Promise<TestResult[]> {
    const params = new URLSearchParams();
    if (page !== undefined) params.append('page', page.toString());
    if (size !== undefined) params.append('size', size.toString());

    const response = await api.get(`/student/results?${params.toString()}`);
    return response.data;
  }

  /**
   * Get a specific test result by ID with questions
   */
  async getTestResult(resultId: string): Promise<any> {
    const response = await api.get(`/student/results/${resultId}`);
    return response.data; // This now returns { result: TestResult, questions: Question[] }
  }

  /**
   * Get test result for a specific test (latest attempt)
   */
  async getTestResultByTestId(testId: string): Promise<TestResult> {
    const response = await api.get(`/student/results/test/${testId}`);
    return response.data;
  }

  /**
   * Get all test attempts for a specific test
   */
  async getAllTestAttempts(testId: string): Promise<TestResult[]> {
    const response = await api.get(`/student/results/test/${testId}/all-attempts`);
    return response.data;
  }

  /**
   * Get recent test results (last 10)
   */
  async getRecentResults(): Promise<TestResult[]> {
    const response = await api.get('/student/results/recent');
    return response.data;
  }

  /**
   * Get test results within a date range
   */
  async getResultsInDateRange(startDate: string, endDate: string): Promise<TestResult[]> {
    const params = new URLSearchParams({
      startDate,
      endDate
    });

    const response = await api.get(`/student/results/date-range?${params.toString()}`);
    return response.data;
  }

  /**
   * Get performance analytics for the student
   */
  async getPerformanceAnalytics(): Promise<StudentPerformanceAnalytics> {
    const response = await api.get('/student/results/analytics');
    return response.data;
  }

  /**
   * Get filtered and sorted results
   */
  async getFilteredResults(filters: ResultsFilter, page = 0, size = 20): Promise<TestResultsResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString()
    });

    // Add filters to params
    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.subject) params.append('subject', filters.subject);
    if (filters.minPercentage !== undefined) params.append('minPercentage', filters.minPercentage.toString());
    if (filters.maxPercentage !== undefined) params.append('maxPercentage', filters.maxPercentage.toString());

    const response = await api.get(`/student/results/filtered?${params.toString()}`);
    return response.data;
  }

  /**
   * Get detailed result with question-by-question analysis
   */
  async getDetailedResult(resultId: string): Promise<DetailedTestResult> {
    const response = await api.get(`/student/results/${resultId}/detailed`);
    return response.data;
  }

  /**
   * Export results to PDF
   */
  async exportResultsToPDF(resultIds: string[]): Promise<Blob> {
    const response = await api.post('/student/results/export/pdf',
      { resultIds },
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * Export results to Excel
   */
  async exportResultsToExcel(resultIds: string[]): Promise<Blob> {
    const response = await api.post('/student/results/export/excel',
      { resultIds },
      { responseType: 'blob' }
    );
    return response.data;
  }

  /**
   * Create a result for an expired test (for analytics purposes)
   */
  async createExpiredTestResult(testId: string): Promise<TestResult> {
    try {
      const response = await api.post(`/student/tests/${testId}/create-expired-result`);
      return response.data;
    } catch (error) {
      // If backend doesn't support this endpoint, create a mock result
      console.warn('Backend does not support expired test results, creating mock result');
      return this.createMockExpiredResult(testId);
    }
  }

  /**
   * Create a mock result for expired tests (fallback)
   */
  private async createMockExpiredResult(testId: string): Promise<TestResult> {
    // Get test details to create a proper mock result
    const testResponse = await api.get(`/student/tests/${testId}`);
    const test = testResponse.data;

    return {
      id: `expired-${testId}-${Date.now()}`,
      testId: testId,
      testTitle: test.title,
      testAssignmentId: test.assignmentId || '',
      studentId: 'current-student',
      score: 0,
      maxScore: test.totalQuestions,
      percentage: 0,
      timeSpent: 0,
      completedAt: new Date().toISOString(),
      startedAt: new Date().toISOString(),
      correctAnswers: 0,
      wrongAnswers: 0,
      incorrectAnswers: 0,
      unansweredQuestions: test.totalQuestions,
      unanswered: test.totalQuestions,
      totalQuestions: test.totalQuestions,
      status: 'expired',
      durationMinutes: 0,
      timeLimitMinutes: test.duration,
      attemptNumber: 1,
      answers: {},
      markedForReview: [],
      subjectResults: [],
      difficultyResult: {
        easy: { totalQuestions: 0, correctAnswers: 0, wrongAnswers: 0, unansweredQuestions: 0, percentage: 0 },
        medium: { totalQuestions: 0, correctAnswers: 0, wrongAnswers: 0, unansweredQuestions: 0, percentage: 0 },
        hard: { totalQuestions: 0, correctAnswers: 0, wrongAnswers: 0, unansweredQuestions: 0, percentage: 0 }
      },
      tenantId: 'current-tenant',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      questions: []
    } as TestResult;
  }

  /**
   * Get comparison data for a specific result
   */
  async getResultComparison(resultId: string): Promise<any> {
    const response = await api.get(`/student/results/${resultId}/comparison`);
    return response.data;
  }

  /**
   * Get performance trends over time
   */
  async getPerformanceTrends(period: 'week' | 'month' | 'quarter' | 'year' = 'month'): Promise<any> {
    const response = await api.get(`/student/results/trends?period=${period}`);
    return response.data;
  }

  /**
   * Get subject-wise performance breakdown
   */
  async getSubjectPerformance(): Promise<any> {
    const response = await api.get('/student/results/subjects');
    return response.data;
  }

  /**
   * Get difficulty-wise performance breakdown
   */
  async getDifficultyPerformance(): Promise<any> {
    const response = await api.get('/student/results/difficulty');
    return response.data;
  }

  /**
   * Check if student has taken a specific test
   */
  async hasStudentTakenTest(testId: string): Promise<boolean> {
    try {
      await this.getTestResultByTestId(testId);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get performance summary for dashboard
   */
  async getPerformanceSummary(): Promise<any> {
    const response = await api.get('/student/results/summary');
    return response.data;
  }

  /**
   * Get rank and percentile information
   */
  async getRankingInfo(testId: string): Promise<any> {
    const response = await api.get(`/student/results/test/${testId}/ranking`);
    return response.data;
  }

  /**
   * Get time analysis for a specific test result
   */
  async getTimeAnalysis(resultId: string): Promise<any> {
    const response = await api.get(`/student/results/${resultId}/time-analysis`);
    return response.data;
  }

  /**
   * Get improvement suggestions based on performance
   */
  async getImprovementSuggestions(resultId?: string): Promise<any> {
    const url = resultId
      ? `/student/results/${resultId}/suggestions`
      : '/student/results/suggestions';

    const response = await api.get(url);
    return response.data;
  }

  /**
   * Get performance comparison with peers
   */
  async getPeerComparison(testId: string): Promise<any> {
    const response = await api.get(`/student/results/test/${testId}/peer-comparison`);
    return response.data;
  }

  /**
   * Get historical performance data
   */
  async getHistoricalPerformance(months = 12): Promise<any> {
    const response = await api.get(`/student/results/historical?months=${months}`);
    return response.data;
  }

  /**
   * Get weak areas analysis
   */
  async getWeakAreasAnalysis(): Promise<any> {
    const response = await api.get('/student/results/weak-areas');
    return response.data;
  }

  /**
   * Get strong areas analysis
   */
  async getStrongAreasAnalysis(): Promise<any> {
    const response = await api.get('/student/results/strong-areas');
    return response.data;
  }

  /**
   * Get test-wise performance comparison
   */
  async getTestComparison(testIds: string[]): Promise<any> {
    const response = await api.post('/student/results/compare-tests', { testIds });
    return response.data;
  }

  /**
   * Get performance goals and achievements
   */
  async getGoalsAndAchievements(): Promise<any> {
    const response = await api.get('/student/results/goals');
    return response.data;
  }

  /**
   * Set performance goals
   */
  async setPerformanceGoals(goals: any): Promise<any> {
    const response = await api.post('/student/results/goals', goals);
    return response.data;
  }

  /**
   * Get study recommendations based on performance
   */
  async getStudyRecommendations(): Promise<any> {
    const response = await api.get('/student/results/study-recommendations');
    return response.data;
  }

  /**
   * Get performance badges and achievements
   */
  async getBadgesAndAchievements(): Promise<any> {
    const response = await api.get('/student/results/badges');
    return response.data;
  }

  /**
   * Share result with parents/teachers
   */
  async shareResult(resultId: string, recipients: string[]): Promise<any> {
    const response = await api.post(`/student/results/${resultId}/share`, { recipients });
    return response.data;
  }

  /**
   * Get shared results access
   */
  async getSharedResults(): Promise<any> {
    const response = await api.get('/student/results/shared');
    return response.data;
  }

  /**
   * Download result certificate
   */
  async downloadCertificate(resultId: string): Promise<Blob> {
    const response = await api.get(`/student/results/${resultId}/certificate`, {
      responseType: 'blob'
    });
    return response.data;
  }
}

export const testResultService = new TestResultService();
